/**
 * BruitCard — exposition au bruit aéronautique (PEB et PGS).
 *
 * UX :
 *   - Header standard EnvCardShell.
 *   - State "ready & count=0" → bandeau vert calme "Hors périmètre" — c'est
 *     ~95 % des fiches en France (la couverture PEB se limite aux abords
 *     d'environ 200 aérodromes).
 *   - State "ready & count≥1" → bandeau coloré par sévérité (rouge PEB A/B,
 *     ambre PEB C, bleu PEB D + PGS), avec un mini-paragraphe pédagogique
 *     précisant les implications réelles (interdiction de construire,
 *     restrictions urbanisme, mention notariale, aide à l'insonorisation).
 *
 * Le ton péda est volontaire : la majorité des particuliers acquéreurs
 * ne connaissent pas la différence PEB/PGS, et les fiches d'agence
 * mentionnent rarement le PEB alors qu'une zone B peut bloquer un crédit.
 */

import type { FicheDoc } from "../../lib/fiches";
import {
  useBruit,
  zoneLabel,
  zoneTone,
  type BruitData,
  type BruitFeature,
} from "../../lib/env/bruit";
import { EnvCardShell } from "./EnvCardShell";
import { Ic } from "../icons";

type Props = { fiche: FicheDoc };

export function BruitCard({ fiche }: Props) {
  const state = useBruit(fiche);
  const subtitle = state.status === "ready" ? summarise(state.data) : undefined;

  return (
    <EnvCardShell
      icon={<Ic.Volume size={18} />}
      title="Bruit aéronautique (PEB / PGS)"
      subtitle={subtitle}
      state={state}
      footer={
        <>
          Source : Géoplateforme · DGAC. Plan d'Exposition au Bruit (PEB,
          urbanisme) et Plan de Gêne Sonore (PGS, aide à l'insonorisation).
          Couverture : abords d'aérodromes uniquement.
        </>
      }
    >
      {state.status === "ready" && <BruitBody data={state.data} />}
    </EnvCardShell>
  );
}

// ─── Subtitle ────────────────────────────────────────────────────────────

function summarise(d: BruitData): string {
  if (d.count === 0) return "Hors périmètre PEB et PGS";
  // `features[0]` est la pire (le fetcher trie par sévérité décroissante),
  // donc équivalent à `worst` en plus complet.
  const top = d.features[0];
  if (!top) return `${d.count} zonage(s) détecté(s)`;
  return `${zoneLabel(top)} · ${top.airport}`;
}

// ─── Body ────────────────────────────────────────────────────────────────

function BruitBody({ data }: { data: BruitData }) {
  if (data.count === 0) {
    return (
      <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-3.5 py-3 flex items-start gap-2.5">
        <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center shrink-0 mt-0.5">
          <Ic.Check size={12} />
        </span>
        <div>
          <div className="text-[13px] font-medium text-emerald-900">
            Hors zone de bruit aéronautique.
          </div>
          <div className="text-[12px] text-emerald-700 mt-0.5 leading-snug">
            La parcelle n'est pas située dans un Plan d'Exposition au Bruit
            (PEB) ni dans un Plan de Gêne Sonore (PGS) — pas de restriction
            d'urbanisme liée au bruit aérien, pas de mention obligatoire dans
            l'acte de vente.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <ul className="space-y-2">
        {data.features.map((f, i) => (
          <BruitLine key={`${f.source}-${f.zone}-${f.oaci}-${i}`} f={f} />
        ))}
      </ul>
    </div>
  );
}

function BruitLine({ f }: { f: BruitFeature }) {
  const tone = zoneTone(f);
  // Mapping tone → palette. On garde la même grammaire de couleurs
  // qu'AbfCard pour que l'utilisateur lise la sévérité d'un coup d'œil
  // entre les deux cartes (vert/ambre/rouge cohérent).
  const palette =
    tone === "danger"
      ? {
          bg: "bg-rose-50 border-rose-200",
          chip: "bg-rose-100 text-rose-700",
          title: "text-rose-900",
          body: "text-rose-800",
          icon: <Ic.AlertTriangle size={12} />,
        }
      : tone === "warn"
        ? {
            bg: "bg-amber-50 border-amber-100",
            chip: "bg-amber-100 text-amber-700",
            title: "text-amber-900",
            body: "text-amber-800",
            icon: <Ic.AlertTriangle size={12} />,
          }
        : {
            bg: "bg-sky-50 border-sky-100",
            chip: "bg-sky-100 text-sky-700",
            title: "text-sky-900",
            body: "text-sky-800",
            icon: <Ic.Info size={12} />,
          };

  return (
    <li
      className={`rounded-lg border ${palette.bg} px-3.5 py-3 flex items-start gap-2.5`}
    >
      <span
        className={`w-5 h-5 rounded-full ${palette.chip} grid place-items-center shrink-0 mt-0.5`}
      >
        {palette.icon}
      </span>
      <div className="min-w-0 flex-1">
        <div className={`text-[13px] font-medium ${palette.title}`}>
          {zoneLabel(f)} · {f.airport}
        </div>
        <div
          className={`text-[12px] ${palette.body} mt-0.5 leading-snug`}
        >
          {implicationLine(f)}
          {f.ldenInt != null && f.ldenExt != null && (
            <>
              {" "}
              Niveau Lden : {f.ldenExt}–{f.ldenInt} dB.
            </>
          )}
        </div>
        <div className="text-[11px] text-ink-600 mt-1 flex items-center gap-2 flex-wrap">
          <span className="font-mono uppercase tracking-wider">{f.oaci}</span>
          {f.arreteDate && (
            <>
              <span className="text-ink-300">·</span>
              <span>arrêté du {fmtDate(f.arreteDate)}</span>
            </>
          )}
          {f.arreteUrl && (
            <>
              <span className="text-ink-300">·</span>
              <a
                href={f.arreteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline decoration-ink-300 hover:text-ink-900"
              >
                arrêté PDF
              </a>
            </>
          )}
        </div>
      </div>
    </li>
  );
}

// Phrase d'implication par zone — ce que l'utilisateur DOIT savoir.
// On reste factuel et bref : la card est une aide à la décision, pas
// un cours de droit de l'urbanisme.
function implicationLine(f: BruitFeature): string {
  if (f.source === "peb") {
    switch (f.zone) {
      case "A":
        return "Zone de bruit fort — construction de logements quasi totalement interdite, extensions limitées au strict minimum.";
      case "B":
        return "Construction et agrandissement de logements interdits. De nombreuses banques refusent d'accorder un crédit en zone B.";
      case "C":
        return "Logements neufs autorisés sous conditions strictes (densité plafonnée, isolation acoustique renforcée).";
      case "D":
      default:
        return "Pas de restriction de construction, mais la zone PEB doit être mentionnée dans l'acte de vente. Effet sur la valorisation à anticiper.";
    }
  }
  // PGS
  return "Logement éligible à l'aide à l'insonorisation (subvention plafonnée à 80 % des travaux d'isolation acoustique).";
}

function fmtDate(iso: string): string {
  // iso est typiquement "YYYY-MM-DD" sans timezone après nettoyage.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${m[3]}/${m[2]}/${m[1]}`;
}
